import type { VueFinalModalProperty } from 'vue-final-modal';

import { getVm } from '@/helpers/vm-helper';
import { VFMPayload } from '@/helpers/vfm/types';

// Global map to track vfmId -> id of pairs
const idGroup = new Map<string, string | undefined>();
let vfmCssLoaded = false;

function getVfm() {
	return getVm().$vfm as VueFinalModalProperty | null;
}

async function ensureVfmCss() {
	if (vfmCssLoaded) return;

	try {
		// @ts-expect-error - CSS import not recognized by TypeScript but works at runtime
		await import(/* webpackChunkName: "vfm-css" */ '@/styles/vfm.scss');
		vfmCssLoaded = true;
	} catch (error) {
		console.error('Failed to load VFM CSS:', error);
	}
}

const open = ({
	type = 'modal',
	props = { escToClose: true },
	onOpened = () => {},
	onBeforeClosed = () => {},
	onClosed = () => {},
	slotComponent,
	slotProps,
	slotId,
	onSlot,
	...options
}: VFMPayload) => {
	let resolve: any, reject: any;
	let isResolved = false;
	let dismiss: any;

	const promise = new Promise(async (_resolve, _reject) => {
		resolve = _resolve;
		reject = _reject;

		const $vfm = getVfm();
		if (!$vfm) {
			_reject('VFM not available');
			throw new Error('VFM not available');
		}

		const vfmId = `${type}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
		idGroup.set(vfmId, slotId);

		dismiss = (data?: any) => {
			$vfm.hide(vfmId);
			_resolve(data);
			isResolved = true;
		};

		$vfm.show({
			component: 'VueFinalModal',
			bind: {
				name: vfmId,
				...props,
			},
			on: {
				opened: () => {
					onOpened();
				},
				'before-close': () => {
					onBeforeClosed();
				},
				closed: () => {
					onClosed();
					idGroup.delete(vfmId);
					if (!isResolved) {
						_resolve(null);
					}
				},
			},
			slots: {
				default: {
					component: slotComponent,
					bind: { ...slotProps },
					on: {
						dismiss,
						...onSlot,
					},
				},
			},
			...options,
		});
	});

	return { promise, resolve, reject, isResolved, dismiss };
};

async function close(vfmId?: string) {
	const $vfm = getVfm();
	if (!$vfm) return;

	try {
		if (vfmId) {
			$vfm.hide(vfmId);
		} else {
			$vfm.hideAll();
		}
	} catch (error) {
		console.error('VFM close error', error);
	}
}

async function closeById(id: string) {
	try {
		for (const [vfmId, contentId] of idGroup.entries()) {
			if (contentId === id) {
				await close(vfmId);
				break;
			}
		}
	} catch (error) {
		console.error('VFM close error', error);
	}
}

export const VFMHelper = { ensureVfmCss, open, close, closeById };
